import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { css } from '@emotion/react';
import { toast } from 'react-toastify';
import { useQuery } from 'react-query';
import { Flex, Text, Spinner, Menu, MenuButton, MenuList, MenuItem, IconButton } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { format, sub, Duration } from 'date-fns';
import { useTranslation } from 'react-i18next';
import * as storage from '../../storage';
import {
  spacing,
  fontStyles,
  colors,
  borderRadius,
  fontFamilies,
  calculateChangeColor,
  safeAreaInset,
} from '../../styles';
import Page from '../Page';
import Header from '../Header';
import { TimeRange, IntervalStats } from '../../types';
import {
  BasicInvestment,
  getBasicInvestments,
  getPortfolioStatistics,
  getPortfolioKeyEvents,
  getWiims,
} from '../../apis/backend';
import FeedbackTakeover from '../FeedbackTakeover';
import * as stores from '../../stores';
import { useRatingDialog } from '../../hooks/useRatingDialog';
import Chart, { ChartNewsData } from '../SymbolPage/Chart';
import { buildDateText } from '../SymbolPage/Chart/utils';
import ColoredPercentChange from '../ColoredPercentChange';
import ReturnMethodologyBottomSheet from '../ReturnMethodologyBottomSheet';
import SkeletonBox from '../SkeletonBox';

// Import SVG icons and Chakra components

// Import our new components
import { InvestmentTable, ColumnSelectionType } from '../InvestmentTable';
import { BREAKPOINTS } from '../../constants/ui';
import ColumnTakeover from '../InvestmentTable/ColumnTakeover';
import Button from '../Button';
import adjustIcon from '../InvestmentTable/adjust.svg';
import infoIcon from '../Icons/info.svg';
import addIcon from '../WatchlistPage/add.svg';
import { ConnectBrokerageButton } from '../Plaid';
import { usePlaidHoldings } from '../../hooks/usePlaidHoldings';
import { usePlaidSnapshots } from '../../hooks/usePlaidSnapshots';
import { usePlaidFeatureFlag } from '../../hooks/useRemoteConfig';
// FeedbackFooter removed - no longer used in this component
import { PortfolioInvestment } from './InvestmentCard';
import { PlaidSingleSnapshotChart } from './PlaidSingleSnapshotChart';
import { getSelectionUpdate, PORTFOLIO_STORAGE_KEY } from './portfolioSelection';
import editIcon from './icons/edit.svg';
import compareIcon from './icons/compare.svg';
import deleteIcon from './icons/delete.svg';
import chatIcon from './icons/chat.svg';
import { selectEventDates, DailyChange } from './chartEventSelection';
// PortfolioFeedCarousel removed - moved to MarketsPage

interface RouteParams {
  id?: string;
}

interface ViewPortfolioProps {
  isOnboarding?: boolean;
  isDefaultView?: boolean;
}

interface InvestmentData extends Partial<BasicInvestment> {
  timeRange?: TimeRange;
}

// Format symbols list with conjunction before the last item
const formatSymbolsList = (symbols: string[], conjunction = 'and'): string => {
  if (symbols.length === 1) return symbols[0];
  if (symbols.length === 2) return `${symbols[0]} ${conjunction} ${symbols[1]}`;
  return `${symbols.slice(0, -1).join(', ')}, ${conjunction} ${symbols[symbols.length - 1]}`;
};

const ViewPortfolio: React.FC<ViewPortfolioProps> = ({ isOnboarding = false, isDefaultView = false }) => {
  const { t } = useTranslation();
  const { id: urlId } = useParams<RouteParams>();
  const history = useHistory();

  // Get portfolio data from store with loading state
  const { portfolios, isLoading: isPortfoliosLoading } = stores.Portfolios.useState();

  // For default view, manage selected portfolio in state with localStorage persistence
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    if (!isDefaultView) return null;
    return localStorage.getItem(PORTFOLIO_STORAGE_KEY);
  });

  // Memoize portfolio IDs to prevent unnecessary effect re-runs when other portfolio props change
  const portfolioIds = useMemo(() => portfolios.map((p) => p.id), [portfolios]);
  const firstPortfolioId = portfolioIds[0] || null;

  // Validate selectedId is still in the portfolio list (handles deleted portfolios)
  const validSelectedId = selectedId && portfolioIds.includes(selectedId) ? selectedId : null;

  // Priority: URL param > valid selected state > first portfolio
  const effectiveId = urlId || validSelectedId || firstPortfolioId;

  // Initialize/validate selection when portfolios load (for default view)
  useEffect(() => {
    if (!isDefaultView || urlId) return;
    if (portfolioIds.length > 0) {
      // Use functional update to access current selectedId without adding it as dependency
      setSelectedId((currentId) => {
        const newId = getSelectionUpdate(portfolioIds, currentId);
        if (newId) {
          localStorage.setItem(PORTFOLIO_STORAGE_KEY, newId);
          return newId;
        }
        return currentId;
      });
    }
  }, [portfolioIds, isDefaultView, urlId]);

  // Plaid holdings for brokerage connection and PnL display
  const {
    isConnected: isPlaidConnected,
    isLoading: isPlaidHoldingsLoading,
    fetchHoldings: refetchPlaidHoldings,
    holdings: plaidHoldings,
    portfolioId: plaidPortfolioId,
    portfolioIds: plaidPortfolioIds,
    portfolioAccountMap,
    disconnect: disconnectPlaid,
    unsupportedSymbols: plaidUnsupportedSymbols,
  } = usePlaidHoldings();

  // Plaid snapshots for historical chart (adaptive - uses all available data)
  const {
    allDataChart: plaidChart,
    startDate: plaidStartDate,
    hasEnoughData: hasPlaidChartData,
    latestSnapshot: plaidLatestSnapshot,
    totalReturn: plaidTotalReturn,
    isLoading: isPlaidSnapshotsLoading,
    reload: reloadPlaidSnapshots,
  } = usePlaidSnapshots();

  // Feature flag for Plaid UI visibility
  const isPlaidEnabled = usePlaidFeatureFlag();

  // Check if current portfolio is a Plaid portfolio (can't manually add/remove stocks)
  const isCurrentPortfolioPlaid = Boolean(
    effectiveId && (plaidPortfolioIds.includes(effectiveId) || plaidPortfolioId === effectiveId),
  );

  // Wait for Plaid status to be determined before fetching backtest data
  // This prevents backtest calls for Plaid portfolios during initial load
  // Status is known when: not connected OR (connected AND done loading)
  // Even if loading fails, we should still enable queries for non-Plaid portfolios
  const isPlaidStatusKnown = !isPlaidConnected || (isPlaidConnected && !isPlaidHoldingsLoading);

  // Save selected portfolio to localStorage when changed
  const handlePortfolioSwitch = useCallback((newId: string) => {
    setSelectedId(newId);
    localStorage.setItem(PORTFOLIO_STORAGE_KEY, newId);
  }, []);

  // Always persist whichever portfolio is currently on screen so reloading
  // the app returns to the same portfolio (not just when the dropdown is used).
  useEffect(() => {
    if (!isOnboarding && effectiveId) {
      localStorage.setItem(PORTFOLIO_STORAGE_KEY, effectiveId);
    }
  }, [effectiveId, isOnboarding]);

  const [investmentsData, setInvestmentsData] = useState<Record<string, InvestmentData>>({});
  //--------------------RATING DIALOG-----------------------//
  // Use the rating hook with isOnboarding as condition to skip
  const { isFeedbackTakeoverShown, setIsFeedbackTakeoverShown, triggerRatingDialog } = useRatingDialog({
    skipCondition: isOnboarding,
    daysToWait: 3,
  });

  useEffect(() => {
    let count = 1;
    try {
      count = parseInt(localStorage.getItem('portfolio_view_count') || '0', 10) + 1;
      localStorage.setItem('portfolio_view_count', count.toString());
    } catch {
      // Ignore localStorage errors (e.g., QuotaExceededError)
    }
    return () => {
      if (count === 2) {
        triggerRatingDialog();
      }
    };
  }, [triggerRatingDialog]);
  //--------------------RATING DIALOG-----------------------//

  // Define available intervals for statistics
  const availableIntervals = ['1w', '1m', '3m', 'ytd', '1y', '5y'];
  const [selectedInterval, setSelectedInterval] = useState<string>('1m');

  // Get selected metric from store but override with our saved/default value
  useEffect(() => {
    // Set the saved or default metric in the store too
    const savedMetric = localStorage.getItem('viewPortfolioMetric') || 'bottomLine';
    stores.TableOptions.update((s) => {
      if (!s.portfolio) s.portfolio = {};
      s.portfolio.selectedMetric = savedMetric;
    });
  }, []);

  const [scannedChartElement, setScannedChartElement] = useState<ChartNewsData | null>(null);
  const [selectedNewsElement, setSelectedNewsElement] = useState<ChartNewsData | null>(null);

  // Table configuration for InvestmentTable
  // Use separate tableId for Plaid vs regular portfolios so each type remembers its own column settings
  const tableId = isCurrentPortfolioPlaid ? 'portfolio-view-plaid' : 'portfolio-view';

  // Default columns based on portfolio type
  const defaultColumns = useMemo(
    () => [
      { type: ColumnSelectionType.Name },
      { type: ColumnSelectionType.Week52Range },
      isCurrentPortfolioPlaid
        ? { type: ColumnSelectionType.ReturnPercent }
        : { type: ColumnSelectionType.ChangePercent, timeRange: TimeRange.weeks1 },
    ],
    [isCurrentPortfolioPlaid],
  );

  // Refs to always have current values in callbacks (avoid stale closures)
  const tableIdRef = useRef(tableId);
  const defaultColumnsRef = useRef(defaultColumns);
  tableIdRef.current = tableId;
  defaultColumnsRef.current = defaultColumns;

  // Subscribe to entire store to ensure we always get fresh data when tableId changes
  const tableOptionsState = stores.TableOptions.useState((s) => s);
  const storedColumnSelections = tableOptionsState[tableId]?.columnSelections;
  const columnSelections = storedColumnSelections || defaultColumns;

  const sortingRule = tableOptionsState[tableId]?.sortingRule || { id: 'name' };

  // State for ColumnTakeover modal
  const [isColumnTakeoverShown, setIsColumnTakeoverShown] = useState(false);

  // Detect small phone screens to hide middle column
  const [isSmallPhone, setIsSmallPhone] = useState(
    typeof window !== 'undefined' && window.innerWidth <= BREAKPOINTS.SMALL_PHONE,
  );
  useEffect(() => {
    const handleResize = () => setIsSmallPhone(window.innerWidth <= BREAKPOINTS.SMALL_PHONE);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Table event handlers - use refs to always get current values (avoid stale closures)
  const handleColumnSelectionChange = useCallback(
    (index: number, value: any) => {
      const currentTableId = tableIdRef.current;
      const currentDefaults = defaultColumnsRef.current;

      stores.TableOptions.update((s) => {
        if (!s[currentTableId]) s[currentTableId] = {};
        // Create new array reference for proper Pullstate change detection
        const existingSelections = s[currentTableId].columnSelections || [...currentDefaults];
        const newSelections = [...existingSelections];
        newSelections[index] = value;
        s[currentTableId].columnSelections = newSelections;
      });
    },
    [], // Empty deps - uses refs for current values
  );

  const handleSortingRuleChange = useCallback((rule: any) => {
    const currentTableId = tableIdRef.current;
    stores.TableOptions.update((s) => {
      if (!s[currentTableId]) s[currentTableId] = {};
      s[currentTableId].sortingRule = rule;
    });
  }, []);

  // Portfolio lookup using effectiveId
  const portfolio = portfolios.find((p) => p.id === effectiveId) || null;

  // Find the TimeRange enum key corresponding to the selectedInterval string
  const currentTimeRange = Object.values(TimeRange).find((v) => v === selectedInterval);

  // Get all symbols from the portfolio (memoized to prevent unnecessary re-renders)
  const allSymbols = useMemo(() => {
    if (!portfolio) return [];
    const allocationSymbols = (portfolio.allocations || []).map((a) => a.symbol);
    const watchlistSymbols = portfolio.symbols || [];
    return [...new Set([...allocationSymbols, ...watchlistSymbols])];
  }, [portfolio]);

  // Fetch investment data using react-query
  const { isLoading: isInvestmentsLoading } = useQuery(
    ['portfolioInvestments', effectiveId],
    async () => {
      if (portfolio && allSymbols.length > 0) {
        return await getBasicInvestments(allSymbols);
      }
      return [];
    },
    {
      enabled: !!portfolio && allSymbols.length > 0,
      onSuccess: (data) => {
        // Convert array to a map indexed by symbol
        const investmentsMap: Record<string, InvestmentData> = {};

        // Set default timeRange and add to map
        data.forEach((inv) => {
          investmentsMap[inv.symbol] = {
            ...inv,
            timeRange: TimeRange.days1,
          };
        });

        setInvestmentsData(investmentsMap);
      },
    },
  );

  // Fetch portfolio key events
  const { data: keyEventsData } = useQuery(
    ['portfolioKeyEvents', effectiveId, allSymbols],
    async () => {
      if (portfolio && allSymbols.length > 0) {
        return await getPortfolioKeyEvents(allSymbols);
      }
      return { symbols: [], events: [], count: 0 };
    },
    {
      enabled: !!portfolio && allSymbols.length > 0,
    },
  );

  // Fetch WIIMs (Why Is It Moving) news for holdings table and chart events.
  // Date range matches the selected chart interval so chart markers span the full period.
  const wiimsDateFrom = useMemo(() => {
    const offsets: Record<string, Duration> = {
      '1d': { weeks: 1 },
      '1w': { weeks: 1 },
      '1m': { months: 1 },
      '3m': { months: 3 },
      ytd: { years: 1 },
      '1y': { years: 1 },
      '5y': { years: 5 },
    };
    return format(sub(new Date(), offsets[selectedInterval] || { weeks: 1 }), 'yyyy-MM-dd');
  }, [selectedInterval]);
  const { data: wiimsData } = useQuery(
    ['investments', 'wiims', { symbols: allSymbols, dateFrom: wiimsDateFrom, pageSize: 100 }],
    () => getWiims(allSymbols, { dateFrom: wiimsDateFrom, pageSize: 100 }),
    {
      enabled: allSymbols.length > 0,
      staleTime: 30000,
      cacheTime: 60000,
    },
  );

  // Define which intervals use each interval_set
  const intradayIntervals = ['1d', '1w', '1m'];
  const dailyIntervals = ['3m', 'ytd', '1y', '5y'];

  // Fetch statistics for intraday intervals (skip for Plaid portfolios - they use snapshots)
  // Wait for isPlaidStatusKnown to avoid backtest calls during Plaid portfolio detection
  const intradayQuery = useQuery<IntervalStats | null, Error>(
    ['portfolioStatistics', effectiveId, 'intraday', selectedInterval],
    () =>
      portfolio && effectiveId
        ? getPortfolioStatistics(effectiveId, 'intraday').then((m) => (m ? m[selectedInterval] : null))
        : Promise.resolve(null),
    {
      enabled:
        !!portfolio &&
        !!effectiveId &&
        isPlaidStatusKnown &&
        !isCurrentPortfolioPlaid &&
        intradayIntervals.includes(selectedInterval),
    },
  );

  // Fetch statistics for daily intervals (skip for Plaid portfolios - they use snapshots)
  // Wait for isPlaidStatusKnown to avoid backtest calls during Plaid portfolio detection
  const dailyQuery = useQuery<IntervalStats | null, Error>(
    ['portfolioStatistics', effectiveId, 'daily', selectedInterval],
    () =>
      portfolio && effectiveId
        ? getPortfolioStatistics(effectiveId, 'daily').then((m) => (m ? m[selectedInterval] : null))
        : Promise.resolve(null),
    {
      enabled:
        !!portfolio &&
        !!effectiveId &&
        isPlaidStatusKnown &&
        !isCurrentPortfolioPlaid &&
        dailyIntervals.includes(selectedInterval),
    },
  );

  // Select the appropriate query based on selectedInterval
  const currentQuery = intradayIntervals.includes(selectedInterval) ? intradayQuery : dailyQuery;
  const stats = currentQuery.data;
  const isStatsLoading = currentQuery.isLoading;
  const statsError = currentQuery.error;
  const refetchPortfolioStats = currentQuery.refetch;

  // Prepare chart data with memoization
  const charts = useMemo(() => {
    const result: Record<TimeRange, ChartElement[] | undefined> = {} as Record<TimeRange, ChartElement[] | undefined>;
    if (!currentTimeRange || !stats?.plot_data) return result;

    const baseValue = stats.plot_data[0]?.portfolio || 0;

    // Helper to extract date in Eastern Time (market timezone) from a date string or Date object.
    // Chart dates come without timezone (e.g., "2024-12-19T09:30:00") and are already in ET.
    // Event dates come with UTC offset (e.g., "2024-12-19T14:30:00+00:00") and need conversion.
    const toEasternDateStr = (dateInput: string | Date): string => {
      if (typeof dateInput === 'string') {
        // Check for timezone indicators: +HH:MM, -HH:MM (after time), or Z
        const hasTimezone = /[+-]\d{2}:\d{2}$/.test(dateInput) || dateInput.endsWith('Z');
        if (!hasTimezone) {
          // No timezone info - already in Eastern Time, extract date directly
          return dateInput.substring(0, 10);
        }
      }
      // Has timezone info - convert to Eastern Time
      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
      return date.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    };

    // Build set of trading dates from chart data
    const tradingDates = new Set<string>();
    for (const d of stats.plot_data) {
      tradingDates.add(toEasternDateStr(d.date));
    }

    // Sort trading dates to find next trading day for weekend/holiday events
    const sortedTradingDates = Array.from(tradingDates).sort();

    // Helper to find next trading day for a given date.
    // Only maps to next trading day if within 4 days (handles weekends + holidays like July 4th).
    // Events older than this are filtered out to avoid misleading users.
    const findNextTradingDay = (dateStr: string): string | null => {
      if (tradingDates.has(dateStr)) return dateStr;
      // Find the first trading day that comes after this date
      for (const tradingDate of sortedTradingDates) {
        if (tradingDate > dateStr) {
          // Check if the gap is reasonable (max 4 days for weekend + holiday)
          const eventDate = new Date(dateStr);
          const tradingDateObj = new Date(tradingDate);
          const daysDiff = (tradingDateObj.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24);
          if (daysDiff <= 4) {
            return tradingDate;
          }
          // Event is too old - don't map it to this trading day
          return null;
        }
      }
      return null;
    };

    // Build map of events by trading date, mapping weekend/holiday events to next trading day.
    // TODO: Select the most relevant event based on user's portfolio (e.g., highest
    // allocation % or biggest price impact) instead of just taking the first one.
    const allEventsByDate = new Map<string, (typeof keyEventsData)['events'][0]>();

    // Insert wiims (news headlines) first so they take priority over key events.
    if (wiimsData?.results) {
      for (const symbolWiims of Object.values(wiimsData.results)) {
        for (const wiim of symbolWiims) {
          const wiimDateStr = toEasternDateStr(wiim.created);
          const tradingDateStr = findNextTradingDay(wiimDateStr);
          if (tradingDateStr && !allEventsByDate.has(tradingDateStr)) {
            allEventsByDate.set(tradingDateStr, {
              id: '',
              title: wiim.title,
              body: wiim.title,
              type: 'wiim',
              symbols: [],
              created_at: wiim.created,
              route: '',
              data: {},
            });
          }
        }
      }
    }

    // Fill in remaining dates with key events (earnings, splits, etc.)
    for (const event of keyEventsData?.events || []) {
      const eventDateStr = toEasternDateStr(event.created_at);
      const tradingDateStr = findNextTradingDay(eventDateStr);

      if (tradingDateStr && !allEventsByDate.has(tradingDateStr)) {
        allEventsByDate.set(tradingDateStr, event);
      }
    }

    // Calculate daily change percent for each day (from previous day's close)
    const dailyChanges: DailyChange[] = [];
    let prevClose: number | null = null;
    let prevDateStr: string | null = null;

    for (const d of stats.plot_data) {
      const dateStr = toEasternDateStr(d.date);
      if (dateStr !== prevDateStr) {
        // New day - calculate change from previous close
        if (prevClose !== null && d.portfolio && allEventsByDate.has(dateStr)) {
          const absChange = Math.abs((d.portfolio - prevClose) / prevClose);
          dailyChanges.push({ dateStr, absChange });
        }
        prevDateStr = dateStr;
      }
      if (d.portfolio) prevClose = d.portfolio;
    }

    // Select events by absolute change, enforcing minimum day gap based on time range
    const selectedDates = selectEventDates(dailyChanges, currentTimeRange);

    // Track which dates have had an event marker assigned (one per day for intraday charts)
    const assignedDates = new Set<string>();

    const chartElements: ChartElement[] = stats.plot_data.map((d) => {
      let minute = '';
      // Parse date string - note: d.date is in Eastern Time but without timezone info
      const dateObj = new Date(d.date);
      if (currentTimeRange === TimeRange.weeks1) {
        // Extract time directly from the ET string (format: "2024-12-19T09:30:00")
        // This avoids timezone conversion issues since the string is already in market time
        const timePart = d.date.substring(11, 16); // "09:30"
        const [hours, mins] = timePart.split(':').map(Number);
        const hour12 = hours % 12 || 12;
        const ampm = hours < 12 ? 'AM' : 'PM';
        minute = `${hour12}:${mins.toString().padStart(2, '0')} ${ampm}`;
      }

      // Only assign event to first data point of selected days
      const dateStr = toEasternDateStr(d.date);
      const event = allEventsByDate.get(dateStr);
      const shouldShowEvent = event && selectedDates.has(dateStr) && !assignedDates.has(dateStr);
      if (shouldShowEvent) {
        assignedDates.add(dateStr);
      }

      return {
        price: d.portfolio,
        benchmark: d.benchmark,
        date: dateObj,
        minute: minute,
        changeRatio: baseValue ? (d.portfolio - baseValue) / baseValue : 0,
        articleHeadline: shouldShowEvent ? event.body : undefined,
        keyEventRoute: shouldShowEvent ? event.route : undefined,
      };
    });
    result[currentTimeRange] = chartElements;
    return result;
  }, [currentTimeRange, stats?.plot_data, keyEventsData?.events, wiimsData?.results]);

  // Function to navigate to chat with portfolio context
  const navigateToChat = (customMessage?: string) => {
    // Build a map of symbol -> allocation percentage from allocations
    const allocationMap = new Map<string, number | undefined>();
    (portfolio?.allocations || []).forEach((alloc) => {
      allocationMap.set(alloc.symbol, alloc.allocation_percentage);
    });

    // Combine symbols from both sources and remove duplicates
    const symbols = portfolio?.symbols || [];
    const allocationSymbols = portfolio?.allocations ? portfolio.allocations.map((alloc) => alloc.symbol) : [];
    const allSymbols = [...new Set([...symbols, ...allocationSymbols])];

    // Format symbols with allocation percentages where available
    const formattedSymbols = allSymbols.map((symbol) => {
      const percentage = allocationMap.get(symbol);
      return percentage != null ? `${symbol} (${Math.round(percentage)}%)` : symbol;
    });

    // Default message
    let message = '';

    // If custom message is provided, use it
    if (customMessage) {
      if (formattedSymbols.length > 0) {
        message = `${customMessage} ${t('portfolios.chatPrompt.thisIsFor', { symbols: formattedSymbols.join(', ') })}`;
      } else {
        message = customMessage;
      }
    }

    // Navigate to chat with the message and source info
    history.push('/chat', { message, from: 'portfolio' });
  };

  // Function to delete the portfolio
  const handleDelete = async () => {
    if (!portfolio || !effectiveId) return;

    // If this is a Plaid portfolio, also disconnect Plaid
    const wasPlaidPortfolio = isCurrentPortfolioPlaid;

    const result = await storage.deletePortfolio(effectiveId);
    if (result.success) {
      const deletedPortfolio = result.deletedPortfolio;

      // Disconnect Plaid connection if this was a Plaid portfolio
      if (wasPlaidPortfolio) {
        await disconnectPlaid();
      }

      // Clear selection and localStorage before navigating (prevents brief "not found" flash)
      if (isDefaultView) {
        setSelectedId(null);
        localStorage.removeItem(PORTFOLIO_STORAGE_KEY);
      }

      // Navigate back to portfolios list
      history.push('/portfolios');

      // For Plaid portfolios, show simple toast (no undo since connection is disconnected)
      // For regular portfolios, show toast with undo button
      if (wasPlaidPortfolio) {
        toast.success(t('portfolios.brokerageDisconnected'));
      } else {
        // Show toast with undo button for regular portfolios
        toast.info(
          () => (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                font-family: ${fontFamilies.helveticaNowText};
                color: ${colors.liquorice};
                background-color: ${colors.sugar};
              `}
            >
              <span>{t('portfolios.deleted')}</span>
              <button
                css={css`
                  margin-left: ${spacing.deci};
                  background: transparent;
                  border: 1px solid ${colors.petrolBlue};
                  color: ${colors.petrolBlue};
                  border-radius: ${borderRadius};
                  padding: ${spacing.milli} ${spacing.centi};
                  cursor: pointer;
                  font-family: ${fontFamilies.helveticaNowText};
                  font-weight: 500;
                  transition: background-color 0.2s ease;

                  &:hover {
                    background-color: rgba(23, 110, 193, 0.1);
                  }

                  html[data-theme='dark'] & {
                    color: ${colors.skyBlue};
                    border-color: ${colors.skyBlue};
                  }
                `}
                onClick={async () => {
                  // Dismiss the current toast notification
                  toast.dismiss();

                  if (deletedPortfolio) {
                    try {
                      const restored = await storage.restorePortfolio(deletedPortfolio);
                      if (restored) {
                        toast.success(() => (
                          <div
                            css={css`
                              display: flex;
                              align-items: center;
                              font-family: ${fontFamilies.helveticaNowText};
                              color: ${colors.liquorice};
                              background-color: ${colors.sugar};
                            `}
                          >
                            <span>{t('portfolios.restored')}</span>
                          </div>
                        ));
                        // Select restored portfolio and navigate to main page
                        localStorage.setItem(PORTFOLIO_STORAGE_KEY, deletedPortfolio.id);
                        history.push('/portfolios');
                      }
                    } catch {
                      toast.error(t('portfolios.failedRestore'));
                    }
                  }
                }}
              >
                {t('portfolios.undo')}
              </button>
            </div>
          ),
          {
            position: toast.POSITION.BOTTOM_CENTER,
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
      }
    } else {
      toast.error(() => (
        <div
          css={css`
            display: flex;
            align-items: center;
            font-family: ${fontFamilies.helveticaNowText};
            color: ${colors.liquorice};
            background-color: ${colors.sugar};
          `}
        >
          {t('portfolios.failedDelete')}
        </div>
      ));
    }
  };

  const [showReturnMethodology, setShowReturnMethodology] = useState(false);

  // Style for the page content
  const pageStyles = css`
    padding-bottom: ${spacing.deci};
    color: var(--text-color);
    max-width: 100%;
    position: relative;
  `;

  const sectionHeadingStyles = css`
    ${fontStyles.lion};
    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `;

  const headerContainer = css`
    margin-bottom: ${spacing.centi};
    position: relative;
  `;

  const mainHeadingStyles = css`
    ${isOnboarding ? fontStyles.elephant : fontStyles.shark};
    margin: 0;
    margin-top: ${spacing.centi};
    margin-bottom: ${spacing.micro};
    color: var(--text-color);
    ${isOnboarding
      ? `
      font-size: 28px;
      line-height: 1.3;
      text-align: center;
    `
      : ''}
  `;

  const chatButtonsRowStyles = css`
    display: flex;
    flex-direction: row;
    gap: ${spacing.centi};
    margin-top: ${spacing.centi};
    margin-bottom: ${spacing.deci};
  `;

  const chatButtonStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: ${spacing.centi} ${spacing.milli};
    background-color: ${colors.snow};
    color: ${colors.petrolBlue};
    border: 0.5px solid ${colors.cloud};
    border-radius: 8px;
    ${fontStyles.flea};
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    gap: ${spacing.micro};
    white-space: nowrap;

    &:hover {
      background-color: ${colors.petrolBlue}10;
    }

    html[data-theme='dark'] & {
      background-color: transparent;
      color: ${colors.skyBlue};
      border-color: ${colors.cloud};
      &:hover {
        background-color: ${colors.skyBlue}10;
      }
    }
  `;

  const chatIconStyles = css`
    width: 16px;
    height: 16px;
    opacity: 0.6;
  `;

  const headingsContainerStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: ${spacing.regular};
  `;

  const chartContainerStyles = css`
    width: 100%;
    margin-top: 0;
    margin-bottom: ${spacing.centi};
  `;

  const chartSectionStyles = css`
    max-width: 800px;
    margin-bottom: 24px;
  `;

  const chartSkeletonStyles = css`
    width: 100%;
    height: 200px;
    margin-bottom: ${spacing.centi};
  `;

  const errorMessageStyles = css`
    color: ${colors.orange};
    text-align: center;
    padding: ${spacing.centi};
    ${fontStyles.dog};
  `;

  const bottomButtonContainerStyles = css`
    padding: 16px;
    border-top: 0.8px solid ${colors.cloud};
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 10;
    background-color: var(--background-color);
    padding-bottom: ${Capacitor.isNativePlatform() ? `calc(16px + ${safeAreaInset.bottom})` : '16px'};
    box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.05);

    @media (min-width: 1024px) {
      width: 1024px;
      left: 50%;
      transform: translateX(-50%);
    }
  `;

  const continueButtonStyles = css`
    width: 100%;
    height: 60px;
    background-color: ${colors.petrolBlue};
    color: ${colors.snow};
    border: none;
    border-radius: 4px;
    font-size: 18.4px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  // Shared styles for Chakra MenuItem components
  const menuItemStyles = css`
    background-color: ${colors.snow} !important;
    color: ${colors.petrolBlue};
    ${fontStyles.dog};
    padding: ${spacing.centi} ${spacing.deci} !important;
    min-height: unset !important;
    &:hover {
      background-color: ${colors.fog} !important;
    }
    html[data-theme='dark'] & {
      background-color: ${colors.liquorice} !important;
      color: ${colors.skyBlue};
      &:hover {
        background-color: ${colors.tungsten} !important;
      }
    }
  `;

  const menuIconStyles = css`
    width: 18px;
    height: 18px;
    margin-right: ${spacing.centi};
  `;

  // Create a map of Plaid holdings by symbol for quick lookup
  // Filter to only holdings for the current portfolio's account
  const plaidHoldingsMap = useMemo(() => {
    const map = new Map<string, (typeof plaidHoldings)[0]>();
    // Get the accountId for the current portfolio (if it's a Plaid portfolio)
    const currentAccountId = effectiveId ? portfolioAccountMap[effectiveId] : undefined;

    plaidHoldings.forEach((holding) => {
      if (holding.symbol && holding.symbol !== 'N/A') {
        // If we know the account, only include holdings from that account
        if (currentAccountId && holding.accountId !== currentAccountId) return;
        map.set(holding.symbol, holding);
      }
    });
    return map;
  }, [plaidHoldings, effectiveId, portfolioAccountMap]);

  // Prepare portfolio investments data by combining API data with allocation data
  const preparePortfolioInvestments = (): PortfolioInvestment[] => {
    // Get all symbols from both sources
    const watchlistSymbols = portfolio?.symbols || [];

    // Create a map of all symbols with their allocation data (if available)
    const symbolsMap = new Map<string, PortfolioInvestment>();

    // Add symbols from allocations (with full data)
    (portfolio?.allocations || []).forEach((alloc) => {
      const investmentData = investmentsData[alloc.symbol];
      const plaidHolding = isCurrentPortfolioPlaid ? plaidHoldingsMap.get(alloc.symbol) : undefined;

      symbolsMap.set(alloc.symbol, {
        ...investmentData,
        symbol: alloc.symbol,
        name: alloc.name || investmentData?.name || alloc.symbol,
        // Only include allocation_percentage if it's actually defined
        ...(alloc.allocation_percentage !== undefined && { allocation_percentage: alloc.allocation_percentage }),
        rationale: alloc.rationale,
        // Add Plaid data if available
        ...(plaidHolding && {
          currentValue: plaidHolding.currentValue,
          costBasis: plaidHolding.costBasis,
          gainLoss: plaidHolding.gainLoss,
          gainLossPercent: plaidHolding.gainLossPercent,
        }),
      });
    });

    // Add symbols from watchlist that aren't already in the map
    watchlistSymbols.forEach((symbol) => {
      if (!symbolsMap.has(symbol)) {
        const investmentData = investmentsData[symbol];
        const plaidHolding = isCurrentPortfolioPlaid ? plaidHoldingsMap.get(symbol) : undefined;

        symbolsMap.set(symbol, {
          ...investmentData,
          symbol,
          name: investmentData?.name || symbol,
          // Add Plaid data if available
          ...(plaidHolding && {
            currentValue: plaidHolding.currentValue,
            costBasis: plaidHolding.costBasis,
            gainLoss: plaidHolding.gainLoss,
            gainLossPercent: plaidHolding.gainLossPercent,
          }),
        });
      }
    });

    // Convert map to array and sort
    return Array.from(symbolsMap.values()).sort((a, b) => {
      // First sort by allocation data presence
      const aHasAllocation = a.allocation_percentage !== undefined;
      const bHasAllocation = b.allocation_percentage !== undefined;

      if (aHasAllocation && !bHasAllocation) return -1;
      if (!aHasAllocation && bHasAllocation) return 1;

      // Then sort by allocation percentage (if both have it)
      if (aHasAllocation && bHasAllocation) {
        return (b.allocation_percentage || 0) - (a.allocation_percentage || 0);
      }

      // Finally sort alphabetically by symbol
      return a.symbol.localeCompare(b.symbol);
    });
  };

  // Get the portfolio investments
  const portfolioInvestments = preparePortfolioInvestments();

  // Memoize portfolio dropdown menu items
  const portfolioMenuItems = useMemo(
    () =>
      portfolios.map((p) => (
        <MenuItem
          key={p.id}
          onClick={() => handlePortfolioSwitch(p.id)}
          css={css`
            ${menuItemStyles}
            ${p.id === effectiveId ? `font-weight: 600;` : ''}
          `}
        >
          {p.title}
        </MenuItem>
      )),
    [portfolios, handlePortfolioSwitch, effectiveId, menuItemStyles],
  );

  // Show loading state if portfolios or investments are still loading
  if (isPortfoliosLoading || isInvestmentsLoading) {
    return (
      <Page showNavigationBar={!isOnboarding}>
        <div css={pageStyles}>
          <div
            css={css`
              ${fontStyles.dog};
              color: var(--secondary-text);
              text-align: center;
              margin-top: ${spacing.deka};
              padding: ${spacing.deka} ${spacing.deci};
            `}
          >
            {t('portfolios.loadingData')}
          </div>
        </div>
      </Page>
    );
  }

  // Empty state: no portfolios exist (only for default view)
  if (isDefaultView && portfolios.length === 0) {
    return (
      <Page showNavigationBar>
        <div css={pageStyles}>
          <div
            css={css`
              text-align: center;
              padding: ${spacing.giga} ${spacing.regular};
              padding-top: calc(30vh);
              min-height: 60vh;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              align-items: center;
            `}
          >
            <div
              css={css`
                ${fontStyles.shark};
                color: var(--text-color);
                margin-bottom: ${spacing.deci};
              `}
            >
              {t('portfolios.noPortfoliosYet')}
            </div>
            <div
              css={css`
                ${fontStyles.dog};
                color: ${colors.silver};
                margin-bottom: ${spacing.regular};
              `}
            >
              {t('portfolios.createToTrack')}
            </div>
            <Button variant="primary" onClick={() => history.push('/portfolios/add')}>
              {t('portfolios.createFirst')}
            </Button>
            {isPlaidEnabled && !isPlaidConnected && (
              <>
                <div
                  css={css`
                    ${fontStyles.mouse};
                    color: ${colors.silver};
                    margin: ${spacing.deci} 0;
                  `}
                >
                  {t('portfolios.or')}
                </div>
                <ConnectBrokerageButton
                  onConnected={async () => {
                    await refetchPlaidHoldings();
                    // Reload snapshots to get fresh data from new brokerage
                    await reloadPlaidSnapshots();
                    // Auto-select the newly connected Plaid portfolio
                    const newSelectedId = localStorage.getItem(PORTFOLIO_STORAGE_KEY);
                    if (newSelectedId) {
                      setSelectedId(newSelectedId);
                    }
                  }}
                />
              </>
            )}
          </div>
        </div>
      </Page>
    );
  }

  if (!portfolio) {
    return (
      <Page showNavigationBar={!isOnboarding}>
        {!isOnboarding && <Header headingText={t('portfolios.notFound')} onBack={() => history.push('/portfolios')} />}
        <div css={pageStyles}>
          <div
            css={css`
              ${fontStyles.dog};
              color: var(--secondary-text);
              text-align: center;
              margin-top: ${spacing.deka};
            `}
          >
            {t('portfolios.notFoundMessage')}
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page showNavigationBar={!isOnboarding}>
      <ReturnMethodologyBottomSheet isShown={showReturnMethodology} onClose={() => setShowReturnMethodology(false)} />
      <FeedbackTakeover isShown={isFeedbackTakeoverShown} onClose={() => setIsFeedbackTakeoverShown(false)} />
      <ColumnTakeover
        key={tableId} // Force re-mount when portfolio type changes
        isShown={isColumnTakeoverShown}
        columnSelections={columnSelections}
        onClose={() => setIsColumnTakeoverShown(false)}
        hideMiddleColumn={isSmallPhone}
        isPlaidPortfolio={isCurrentPortfolioPlaid}
        onChange={(index, value) => {
          if (!columnSelections) return;
          const indexOfDuplicate = columnSelections.findIndex((selection) => {
            if (value.type === ColumnSelectionType.ChangePercent)
              return value.type === selection.type && value.timeRange === selection.timeRange;
            return value.type === selection.type;
          });
          if (indexOfDuplicate >= 0 && indexOfDuplicate !== index) {
            handleColumnSelectionChange(indexOfDuplicate, columnSelections[index]);
          }
          handleColumnSelectionChange(index, value);
        }}
      />
      {!isOnboarding && (
        <div
          css={css`
            padding-top: ${spacing.deci};
          `}
        >
          <Header
            css={css`
              border-bottom: 0.5px solid ${colors.cloud};
              html[data-theme='dark'] & {
                border-bottom-color: ${colors.tungsten};
              }
            `}
            headingText={
              isDefaultView && portfolios.length >= 2 ? (
                <Menu>
                  <MenuButton
                    css={css`
                      ${fontStyles.shark};
                      margin: 0;
                      padding: 0;
                      background: transparent;
                      border: none;
                      color: var(--text-color);
                      cursor: pointer;
                    `}
                    aria-label={t('portfolios.selectPortfolio')}
                  >
                    <span
                      css={css`
                        display: inline-flex;
                        align-items: center;
                        gap: ${spacing.micro};
                      `}
                    >
                      {portfolio.title || 'Portfolio'}
                      <ChevronDownIcon boxSize={5} />
                    </span>
                  </MenuButton>
                  <MenuList
                    css={css`
                      background-color: var(--card-background);
                      border-color: var(--border-color);
                    `}
                  >
                    {portfolioMenuItems}
                  </MenuList>
                </Menu>
              ) : (
                portfolio.title || 'Portfolio'
              )
            }
            variant="left-aligned"
            hideBackButton
            rightAction={
              <Menu>
                <MenuButton
                  as={IconButton}
                  aria-label={t('portfolios.options')}
                  icon={
                    <img
                      src={editIcon}
                      alt="Options"
                      css={css`
                        width: 23px;
                        height: 23px;
                        filter: var(--icon-filter);
                      `}
                    />
                  }
                  variant="ghost"
                  size="md"
                  minW="44px"
                  minH="44px"
                  css={css`
                    background-color: transparent !important;
                    &:hover {
                      background-color: rgba(23, 110, 193, 0.1) !important;
                    }
                  `}
                />
                <MenuList
                  css={css`
                    background-color: var(--card-background);
                    border-color: var(--border-color);
                  `}
                >
                  {!isCurrentPortfolioPlaid && (
                    <MenuItem onClick={() => history.push(`/portfolios/edit/${portfolio.id}`)} css={menuItemStyles}>
                      <img src={editIcon} alt="" css={menuIconStyles} />
                      {t('portfolios.editPortfolio')}
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => history.push('/portfolios/add')} css={menuItemStyles}>
                    <img src={addIcon} alt="" css={menuIconStyles} />
                    {t('portfolios.addPortfolio')}
                  </MenuItem>
                  {isDefaultView && portfolios.length > 1 && (
                    <MenuItem onClick={() => history.push('/portfolios/compare')} css={menuItemStyles}>
                      <img src={compareIcon} alt="" css={menuIconStyles} />
                      {t('portfolios.comparePortfolios')}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={handleDelete}
                    css={css`
                      ${menuItemStyles}
                      color: ${colors.berry};
                    `}
                  >
                    <img src={deleteIcon} alt="" css={menuIconStyles} />
                    {t('portfolios.deletePortfolio')}
                  </MenuItem>
                </MenuList>
              </Menu>
            }
          />
        </div>
      )}
      <div css={pageStyles}>
        {/* Header with title - only show for onboarding */}
        {isOnboarding && (
          <div css={headerContainer}>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore: workaround for complex union type error in Flex children */}
            <Flex justify="center" align="center">
              <h1 css={mainHeadingStyles}>{t('portfolios.createdForYou')}</h1>
            </Flex>
          </div>
        )}

        {/* Only show Growth, Strategy, and Holdings sections if there are tickers */}
        {allSymbols.length > 0 && (
          <>
            {/* Performance Chart and Interval Selector - Moved Here */}
            <div css={chartSectionStyles}>
              {/* Statistics Content (Chart Part) - Moved Here */}
              {/* Plaid portfolios: loading -> single snapshot -> adaptive chart -> fall through to standard chart */}
              {isCurrentPortfolioPlaid && isPlaidSnapshotsLoading ? (
                // Loading state for Plaid - show spinner while snapshots are loading
                // This prevents showing the standard chart during the loading race condition
                <Flex justify="center" py={4}>
                  <Spinner size="md" color={colors.petrolBlue} />
                </Flex>
              ) : isCurrentPortfolioPlaid && !hasPlaidChartData && plaidLatestSnapshot ? (
                // Less than 5 snapshots: Show single point chart with latest value
                <PlaidSingleSnapshotChart
                  totalValue={plaidLatestSnapshot.totalValue}
                  snapshotDate={new Date(plaidLatestSnapshot.date)}
                />
              ) : isCurrentPortfolioPlaid && hasPlaidChartData && plaidChart && plaidChart.length > 0 ? (
                // Plaid portfolio chart - adaptive chart using all available data
                <div css={chartContainerStyles}>
                  <Text
                    css={css`
                      margin-bottom: ${spacing.centi};
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <span
                      css={css`
                        ${fontStyles.shark};
                        color: ${colors.liquorice};
                        margin-right: ${spacing.centi};
                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                      `}
                    >
                      {scannedChartElement
                        ? new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(scannedChartElement.price)
                        : plaidLatestSnapshot
                          ? new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(plaidLatestSnapshot.totalValue)
                          : ''}
                    </span>
                    <ColoredPercentChange
                      value={scannedChartElement ? (scannedChartElement.changeRatio ?? undefined) : plaidTotalReturn}
                    />
                    <span
                      css={css`
                        margin-left: ${spacing.centi};
                        color: ${colors.silver};
                        ${fontStyles.flea};
                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                      `}
                    >
                      {scannedChartElement
                        ? format(scannedChartElement.date, 'MMM d, yyyy')
                        : plaidStartDate
                          ? t('portfolios.since', { date: format(plaidStartDate, 'MMM d') })
                          : ''}
                    </span>
                    <img
                      src={infoIcon}
                      alt={t('portfolios.howReturnCalculated')}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowReturnMethodology(true)}
                      onKeyDown={(e) => e.key === 'Enter' && setShowReturnMethodology(true)}
                      css={css`
                        margin-left: ${spacing.centi};
                        width: 22px;
                        height: 22px;
                        cursor: pointer;
                        opacity: 0.7;
                        &:hover {
                          opacity: 1;
                        }
                      `}
                    />
                  </Text>
                  <Chart
                    strokeColor={calculateChangeColor(plaidTotalReturn)}
                    charts={{ [TimeRange.years5]: plaidChart }}
                    isLoading={false}
                    isError={false}
                    onRetry={() => undefined}
                    timeRange={TimeRange.years5}
                    selectedNewsElement={selectedNewsElement ?? undefined}
                    onSelectNews={(el) => {
                      setSelectedNewsElement(el || null);
                    }}
                    onScanPrice={(el) => {
                      if (el) {
                        setScannedChartElement(el);
                      } else {
                        setScannedChartElement(null);
                      }
                    }}
                    scannedPrice={scannedChartElement ?? undefined}
                    ticker={portfolio.id}
                    showWiim={false}
                    height={120}
                    isAdaptiveChart={true}
                  />
                </div>
              ) : isStatsLoading ? (
                isOnboarding ? (
                  <div css={chartSkeletonStyles}>
                    <SkeletonBox width="100%" />
                  </div>
                ) : (
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore: suppress complex union type error
                  <Flex justify="center" py={4}>
                    <Spinner size="md" color={colors.petrolBlue} />
                  </Flex>
                )
              ) : statsError ? (
                <div css={errorMessageStyles}>{t('portfolios.unableToLoadStats')}</div>
              ) : stats ? (
                <>
                  {/* Performance Chart - Conditionally render based on plot_data and time range */}
                  {stats && currentTimeRange && stats.plot_data && stats.plot_data.length > 0 && (
                    <div css={chartContainerStyles}>
                      {/* Updated the Text display logic to always show percentage and conditionally show date text */}
                      {stats && currentTimeRange && charts[currentTimeRange] && (
                        <Text
                          css={css`
                            margin-bottom: ${spacing.centi};
                            display: flex;
                            align-items: center;
                          `}
                        >
                          {/* Always display ColoredPercentChange */}
                          <ColoredPercentChange
                            value={
                              scannedChartElement ? (scannedChartElement.changeRatio ?? undefined) : stats.total_return
                            }
                            // timeRange prop is no longer needed here, buildDateText handles it
                          />
                          {/* Display date text for all time ranges now */}
                          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                          {/* @ts-ignore: Ignore complex union type for buildDateText arguments */}
                          <span
                            css={css`
                              margin-left: ${spacing.centi};
                              color: ${colors.silver};
                              ${fontStyles.flea};
                            `}
                          >
                            {buildDateText(
                              charts[currentTimeRange]!,
                              currentTimeRange,
                              scannedChartElement ?? undefined,
                            )}
                          </span>
                          <img
                            src={infoIcon}
                            alt={t('portfolios.howReturnCalculated')}
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowReturnMethodology(true)}
                            onKeyDown={(e) => e.key === 'Enter' && setShowReturnMethodology(true)}
                            css={css`
                              margin-left: ${spacing.centi};
                              width: 22px;
                              height: 22px;
                              cursor: pointer;
                              opacity: 0.7;
                              &:hover {
                                opacity: 1;
                              }
                            `}
                          />
                        </Text>
                      )}
                      <Chart
                        strokeColor={calculateChangeColor(stats.total_return)}
                        charts={charts}
                        isLoading={isOnboarding ? false : isStatsLoading}
                        isError={!!statsError}
                        onRetry={() => refetchPortfolioStats()}
                        timeRange={currentTimeRange}
                        selectedNewsElement={selectedNewsElement ?? undefined}
                        onSelectNews={(el) => {
                          setSelectedNewsElement(el || null);
                        }}
                        onScanPrice={(el) => {
                          if (el) {
                            setScannedChartElement(el);
                          } else {
                            setScannedChartElement(null);
                          }
                        }}
                        scannedPrice={scannedChartElement ?? undefined}
                        ticker={portfolio.id}
                        showWiim={true}
                        height={120}
                      />
                    </div>
                  )}
                  {/* Show message if chart data specifically is missing (covers YTD case too if plot_data is empty) */}
                  {stats && (!stats.plot_data || stats.plot_data.length === 0) && (
                    <div css={errorMessageStyles}>{t('portfolios.historicalUnavailable')}</div>
                  )}
                </>
              ) : (
                <div css={errorMessageStyles}>{t('portfolios.statsUnavailable')}</div>
              )}

              {/* Interval Selector - Hidden for all Plaid portfolios (adaptive chart with no time ranges) */}
              {!isCurrentPortfolioPlaid && (
                <div
                  css={css`
                    display: flex;
                    justify-content: center;
                    margin-top: ${spacing.deci};
                    margin-bottom: ${spacing.deci};
                    flex-wrap: nowrap;
                    gap: ${spacing.centi};

                    @media (max-width: 400px) {
                      gap: ${spacing.milli};
                    }
                  `}
                  role="radiogroup"
                >
                  {availableIntervals.map((interval) => {
                    const isChecked = selectedInterval === interval;
                    return (
                      <label
                        key={interval}
                        css={[
                          css`
                            position: relative;
                            border: var(--border-color) 2px solid;
                            color: var(--secondary-text);
                            background-color: ${colors.snow};
                            padding: ${spacing.milli} ${spacing.centi};
                            min-width: 40px;
                            text-align: center;
                            border-radius: 24px;
                            cursor: pointer;
                            -webkit-tap-highlight-color: transparent;
                            ${fontStyles.fairyfly};
                            transition: all 0.2s;

                            @media (max-width: 400px) {
                              min-width: 32px;
                              padding: ${spacing.micro} ${spacing.milli};
                            }

                            &:hover {
                              background-color: rgba(0, 0, 0, 0.05);
                            }

                            html[data-theme='dark'] & {
                              background-color: ${colors.liquorice};
                              &:hover {
                                background-color: rgba(255, 255, 255, 0.05);
                              }
                            }
                          `,
                          isChecked &&
                            css`
                              color: ${colors.petrolBlue};
                              border-color: ${colors.petrolBlue};

                              html[data-theme='dark'] & {
                                color: ${colors.skyBlue};
                                border-color: ${colors.skyBlue};
                              }
                            `,
                        ]}
                      >
                        {interval.toUpperCase()}
                        <input
                          type="radio"
                          value={interval}
                          checked={isChecked}
                          onChange={() => setSelectedInterval(interval)}
                          css={css`
                            position: absolute;
                            visibility: hidden;
                          `}
                        />
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Holdings section */}
            <div
              css={css`
                ${headingsContainerStyles};
                margin-top: 16px;
              `}
            >
              <h2 css={sectionHeadingStyles}>{t('portfolios.holdings')}</h2>
              <Button
                onClick={() => setIsColumnTakeoverShown(true)}
                css={css`
                  padding: ${spacing.deci} 0;
                  ${fontStyles.flea}
                `}
              >
                <img
                  src={adjustIcon}
                  css={css`
                    vertical-align: middle;
                    margin-right: ${spacing.milli};
                  `}
                />
                {t('portfolios.changeColumns')}
              </Button>
            </div>
          </>
        )}

        {portfolioInvestments.length === 0 ? (
          <div
            css={css`
              text-align: center;
              padding: ${spacing.giga} ${spacing.regular};
              padding-top: calc(50vh - 200px);
              min-height: 60vh;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
            `}
          >
            {isOnboarding ? (
              <div
                css={css`
                  margin-bottom: ${spacing.regular};
                `}
              >
                <div
                  css={css`
                    ${fontStyles.shark};
                    color: ${colors.liquorice};
                    margin-bottom: ${spacing.deci};

                    html[data-theme='dark'] & {
                      color: ${colors.sugar};
                    }
                  `}
                >
                  {t('portfolios.somethingWentWrong')}
                </div>
                <div
                  css={css`
                    ${fontStyles.dog};
                    color: ${colors.silver};
                    margin-bottom: ${spacing.kilo};
                  `}
                >
                  {t('portfolios.couldNotGenerate')}
                </div>
              </div>
            ) : (
              <div
                css={css`
                  ${fontStyles.dog};
                  color: ${colors.silver};
                  margin-bottom: ${spacing.kilo};
                  text-align: center;
                `}
              >
                {t('portfolios.noInvestments')}
              </div>
            )}

            {!isOnboarding && !isCurrentPortfolioPlaid && (
              <button
                css={css`
                  display: block;
                  width: 100%;
                  max-width: 280px;
                  margin: 0 auto;
                  background-color: ${colors.petrolBlue};
                  color: ${colors.snow};
                  border: none;
                  border-radius: 8px;
                  padding: ${spacing.deci} ${spacing.regular};
                  ${fontStyles.horse};
                  font-weight: 600;
                  cursor: pointer;
                  transition: all 0.2s ease;

                  &:hover {
                    opacity: 0.9;
                    transform: translateY(-1px);
                  }
                `}
                onClick={() => history.push(`/portfolios/${effectiveId}/add-tickers`)}
              >
                {t('portfolios.addInvestments')}
              </button>
            )}

            {isOnboarding && (
              <button
                css={css`
                  display: block;
                  width: 100%;
                  background-color: ${colors.petrolBlue};
                  color: ${colors.snow};
                  border: none;
                  border-radius: 8px;
                  padding: ${spacing.deci};
                  ${fontStyles.horse};
                  margin-top: ${spacing.deci};
                  font-weight: 500;
                  cursor: pointer;
                  margin: ${spacing.deci} auto;
                  max-width: 300px;
                `}
                onClick={() => history.push('/onboarding')}
              >
                {t('portfolios.tryAgain')}
              </button>
            )}
          </div>
        ) : (
          <div>
            <InvestmentTable
              investments={portfolioInvestments}
              wiims={wiimsData?.results || {}}
              columnSelections={columnSelections}
              onChangeColumnSelections={handleColumnSelectionChange}
              sortingRule={sortingRule}
              onChangeSortingRule={handleSortingRuleChange}
              isLoading={isInvestmentsLoading}
              // Filters disabled - all holdings always visible in portfolio view
              filters={undefined}
              onSetFilter={undefined}
              hideColumnControls={true}
            />
          </div>
        )}

        {/* Add to portfolio button - hidden for Plaid portfolios which are synced from brokerage */}
        {!isOnboarding && !isCurrentPortfolioPlaid && (
          <button
            type="button"
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              padding: ${spacing.centi} ${spacing.regular};
              margin-top: ${spacing.deci};
              background-color: ${colors.petrolBlue};
              color: ${colors.snow};
              border: none;
              border-radius: 8px;
              ${fontStyles.cat};
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;

              &:hover {
                opacity: 0.9;
              }

              html[data-theme='dark'] & {
                background-color: ${colors.skyBlue};
                color: ${colors.liquorice};
              }
            `}
            onClick={() => history.push(`/portfolios/${effectiveId}/add-tickers`)}
          >
            {t('portfolios.addToPortfolio')}
          </button>
        )}
        {/* AI Chat Section */}
        {!isOnboarding && allSymbols.length > 0 && (
          <div css={chatButtonsRowStyles}>
            <button
              type="button"
              css={chatButtonStyles}
              onClick={() => navigateToChat(t('portfolios.chatPrompt.catalysts'))}
            >
              <img src={chatIcon} alt="" css={chatIconStyles} />
              {t('portfolios.findCatalysts')}
            </button>
            <button
              type="button"
              css={chatButtonStyles}
              onClick={() => navigateToChat(t('portfolios.chatPrompt.risks'))}
            >
              <img src={chatIcon} alt="" css={chatIconStyles} />
              {t('portfolios.checkRisks')}
            </button>
            <button
              type="button"
              css={chatButtonStyles}
              onClick={() => navigateToChat(t('portfolios.chatPrompt.improve'))}
            >
              <img src={chatIcon} alt="" css={chatIconStyles} />
              {t('portfolios.getIdeas')}
            </button>
          </div>
        )}
        {/* Connect brokerage button - shown when Plaid is enabled and not connected */}
        {isPlaidEnabled && !isOnboarding && !isPlaidConnected && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              margin-top: ${spacing.regular};
              padding-top: ${spacing.deci};
              border-top: 1px solid var(--border-color);
            `}
          >
            <div
              css={css`
                ${fontStyles.mouse};
                color: ${colors.silver};
                margin-bottom: ${spacing.centi};
              `}
            >
              {t('portfolios.or')}
            </div>
            <ConnectBrokerageButton
              onConnected={async () => {
                await refetchPlaidHoldings();
                // Reload snapshots to get fresh data from new brokerage
                await reloadPlaidSnapshots();
                // Auto-select the newly connected Plaid portfolio
                const newSelectedId = localStorage.getItem(PORTFOLIO_STORAGE_KEY);
                if (newSelectedId) {
                  setSelectedId(newSelectedId);
                }
              }}
            />
          </div>
        )}

        {/* Unsupported symbols notice for Plaid portfolios */}
        {isCurrentPortfolioPlaid && plaidUnsupportedSymbols.length > 0 && (
          <p
            css={css`
              ${fontStyles.fairyfly};
              color: ${colors.silver};
              text-align: center;
              margin-top: ${spacing.deci};
            `}
          >
            {plaidUnsupportedSymbols.length === 1
              ? t('portfolios.symbolNotSupported', { symbol: plaidUnsupportedSymbols[0] })
              : t('portfolios.symbolsNotSupported', {
                  symbols: formatSymbolsList(plaidUnsupportedSymbols, t('common.and')),
                })}
          </p>
        )}
      </div>
      {isOnboarding && (
        <div css={bottomButtonContainerStyles}>
          <button
            css={continueButtonStyles}
            onClick={() => {
              stores.showPaymentModalByPreference('onboarding_portfolio_continue');
            }}
          >
            {t('portfolios.continue')}
          </button>
        </div>
      )}
    </Page>
  );
};

export default ViewPortfolio;
